import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BookOpen, RefreshCw, ChevronDown, ChevronRight, Filter, ChevronLeft, ChevronsLeft, ChevronsRight } from 'lucide-react';
import api from './axiosConfig';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useFontSize } from './FontSizeContext';

// 🎯 دفتر اليومية - عرض فقط، القيود كلها بتترحّل أوتوماتيك من العمليات
// (مخزون، قفل أمر شغل، إهلاك) مفيش شاشة إدخال قيد يدوي في النسخة دي
const GLEntryList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const { fontSize } = useFontSize();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedId, setExpandedId] = useState(null);

  const page = parseInt(searchParams.get('page')) || 1;
  const limit = 30;
  const source = searchParams.get('source') || '';

  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionOrg = session.org_id || '';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/gl/entries', { params: { org_id: sessionOrg, source: source || undefined, page, limit } });
      setData(res.data.data);
      setTotalCount(res.data.total);
    } catch (err) {
      toast.error(t('common.load_error', 'Failed to load GL entries'));
    } finally {
      setLoading(false);
    }
  }, [sessionOrg, source, page, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateFilters = (newFilters) => {
    const params = Object.fromEntries(searchParams.entries());
    const updated = { ...params, ...newFilters };
    Object.keys(updated).forEach(key => !updated[key] && delete updated[key]);
    setSearchParams(updated);
  };

  const totalPages = Math.ceil(totalCount / limit) || 1;

  return (
    <div className={`flex flex-col h-full bg-surface-app uppercase tracking-tighter overflow-hidden ${isRTL ? 'font-cairo' : 'font-sans'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-surface-card p-3 border-b shadow-sm z-20">
        <div className="flex justify-between items-center px-4">
          <h2 className="text-primary-dark font-black italic text-lg flex items-center gap-2">
            <BookOpen size={22} className="text-status-warning" />
            {t('gl.entries.title', 'GL Entries')}
            <RefreshCw size={18} className={`cursor-pointer transition-all ${loading ? 'animate-spin text-status-warning' : 'text-primary'} ${isRTL ? 'mr-2' : 'ml-2'}`} onClick={fetchData} />
          </h2>
          <select
            className="border border-line rounded-sm px-3 py-1.5 font-bold text-primary outline-none"
            value={source}
            onChange={e => updateFilters({ source: e.target.value, page: 1 })}
          >
            <option value="">{t('gl.entries.all_sources', 'All Sources')}</option>
            <option value="INVENTORY">INVENTORY</option>
            <option value="WORKORDER">WORKORDER</option>
            <option value="DEPRECIATION">DEPRECIATION</option>
          </select>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 custom-scrollbar space-y-2">
        {loading ? (
          <div className="p-20 text-center font-black text-ink-secondary animate-pulse tracking-[0.2em]">{t('common.syncing', 'Syncing Data...')}</div>
        ) : data.length === 0 ? (
          <div className="p-20 text-center font-black text-ink-secondary italic uppercase">{t('gl.entries.no_records', 'No GL Entries Found')}</div>
        ) : (
          data.map(entry => (
            <div key={entry.entry_num} className="bg-surface-card border rounded-sm shadow-sm overflow-hidden">
              <div
                className="flex items-center justify-between p-3 cursor-pointer hover:bg-primary-soft transition-colors"
                onClick={() => setExpandedId(expandedId === entry.entry_num ? null : entry.entry_num)}
              >
                <div className="flex items-center gap-3">
                  {expandedId === entry.entry_num ? <ChevronDown size={14}/> : <ChevronRight size={14} className={isRTL ? 'rotate-180' : ''}/>}
                  <span className="font-black text-primary">{entry.entry_num}</span>
                  <span className="bg-status-warning-bg text-status-warning px-2 py-0.5 rounded-full text-[9px]">{entry.source}</span>
                  <span className="text-ink-secondary normal-case italic">{entry.description}</span>
                </div>
                <div className="flex items-center gap-6 text-ink-secondary">
                  <span>{new Date(entry.entry_date).toLocaleDateString()}</span>
                  <span className="font-black text-status-success">{Number(entry.total_debit).toLocaleString()}</span>
                </div>
              </div>
              {expandedId === entry.entry_num && (
                <table className="w-full border-t">
                  <thead className="bg-surface-panel text-ink-secondary">
                    <tr>
                      <th className="p-2 text-left">{t('gl.entries.fields.account', 'Account')}</th>
                      <th className="p-2 text-left">{t('gl.entries.fields.line_desc', 'Description')}</th>
                      <th className="p-2 text-center w-32">{t('gl.entries.fields.debit', 'Debit')}</th>
                      <th className="p-2 text-center w-32">{t('gl.entries.fields.credit', 'Credit')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(entry.lines || []).map(line => (
                      <tr key={line.id} className="border-t">
                        <td className="p-2 font-bold text-primary">{line.glaccount}</td>
                        <td className="p-2 text-ink-secondary normal-case italic">{line.description}</td>
                        <td className="p-2 text-center text-status-success font-bold">{line.debit > 0 ? Number(line.debit).toLocaleString() : ''}</td>
                        <td className="p-2 text-center text-status-danger font-bold">{line.credit > 0 ? Number(line.credit).toLocaleString() : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))
        )}
      </div>

      <div className={`bg-primary-dark text-white p-2 px-8 flex justify-between items-center shadow-inner z-30 font-black text-[10px] ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Filter size={14} className="text-status-warning"/>
          <span>{t('common.total', 'Total')}: <span className="text-status-warning">{totalCount}</span></span>
        </div>
        <div className={`flex items-center gap-1 bg-black/20 rounded px-2 py-1 border border-white/10 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <button disabled={page === 1} onClick={() => updateFilters({page: 1})} className="p-1.5 hover:text-status-warning disabled:opacity-20">
            <ChevronsLeft size={16} className={isRTL ? 'rotate-180' : ''}/>
          </button>
          <button disabled={page === 1} onClick={() => updateFilters({page: page - 1})} className="p-1.5 hover:text-status-warning disabled:opacity-20">
            <ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/>
          </button>
          <div className="px-4 text-[11px] font-mono border-x border-white/10 mx-1">
            <span className="text-status-warning text-sm">{page}</span> / {totalPages}
          </div>
          <button disabled={page >= totalPages} onClick={() => updateFilters({page: page + 1})} className="p-1.5 hover:text-status-warning disabled:opacity-20">
            <ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/>
          </button>
          <button disabled={page >= totalPages} onClick={() => updateFilters({page: totalPages})} className="p-1.5 hover:text-status-warning disabled:opacity-20">
            <ChevronsRight size={16} className={isRTL ? 'rotate-180' : ''}/>
          </button>
        </div>
      </div>
    </div>
  );
};

export default GLEntryList;
